import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConversation } from '@11labs/react';
import { useAudit } from './LegalContext';
import { FeedbackMessage } from './types';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume,
  faGavel
} from '@fortawesome/free-solid-svg-icons';
import { getHVASignedUrl } from 'services/hvaagent.service';

// Interfaces for messages and conversation status
interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
  timestamp: Date;
}

interface ConversationMode {
  mode: 'speaking' | 'listening';
}

// Props for the widget component
type AuditWidgetProps = {
  clientName: string;
  clientCompany: string; // Kept for compatibility but not used
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback?: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
};

/**
 * Conversation widget that handles verbal interaction with the virtual agent.
 */
const AuditWidget = ({
  clientName,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  clientCompany,
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice
}: AuditWidgetProps): JSX.Element => {
  // State for permissions and conversation
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});
  const [isListening, setIsListening] = useState(false);

  // Get full conversation context
  const {
    selectedClient,
    conversationStage,
    setConversationStage,
    addSystemMessage,
    addLegalInformation,
    agentInstructions
  } = useAudit();

  // Reference for messages container (for automatic scrolling)
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to last message
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Update clientTools whenever related states change
  useEffect(() => {
    // Create client tools configuration that the agent can use
    setClientToolsConfig({
      // To report legal information and objectives
      // eslint-disable-next-line
      reportLegalInfo: async (parameters: { objective: string; legalSources: any[]; applicationPlan: string; category?: string }) => {
        const { objective, legalSources, applicationPlan, category } =
          parameters;

        try {
          // Add legal information to context
          addLegalInformation({
            objective,
            legalSources: legalSources.map(source => ({
              source: source.source || 'Fuente no especificada',
              relevance: source.relevance || 'Relevancia no especificada',
              content: source.content || 'Contenido no disponible'
            })),
            applicationPlan,
            category
          });

          // Add feedback message
          if (onFeedback) {
            onFeedback({
              type: 'legal',
              message: 'Información legal registrada',
              details: `Objetivo: ${objective}`,
              success: true
            });
          }

          // Add system message
          addSystemMessage(
            'legal',
            'Información legal registrada',
            `${legalSources.length} fuentes consultadas`
          );

          return JSON.stringify({
            success: true,
            message: 'Información legal registrada correctamente'
          });
        } catch (error) {
          console.error('Error registrando información legal:', error);

          if (onFeedback) {
            onFeedback({
              type: 'legal',
              message: 'Error al registrar información legal',
              success: false
            });
          }

          return JSON.stringify({
            success: false,
            message: 'Error al registrar la información legal'
          });
        }
      },

      // To request information from the system
      // eslint-disable-next-line
      requestInfo: async (parameters: { requestedInfo: string; filters?: any }) => {
        const { requestedInfo } = parameters;

        switch (requestedInfo) {
          // Request current date and time
          case 'DATETIME': {
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('es-ES', options);

            addSystemMessage('info', 'Fecha y hora actual solicitada');

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          case 'CLIENTINFO': {
            if (!selectedClient) {
              return JSON.stringify({
                success: false,
                message: 'No hay cliente seleccionado'
              });
            }

            addSystemMessage('info', 'Información del cliente solicitada');

            return JSON.stringify({
              success: true,
              data: {
                id: selectedClient.id,
                name: selectedClient.name,
                firstSurname: selectedClient.firstSurname,
                secondSurname: selectedClient.secondSurname,
                email: selectedClient.email,
                phoneNumber: selectedClient.phoneNumber
              },
              message: ''
            });
          }

          // Default for uncovered information requests
          default: {
            addSystemMessage(
              'info',
              `Información no disponible: ${requestedInfo}`,
              'Tipo de información solicitada no disponible'
            );

            return JSON.stringify({
              success: false,
              data: null,
              message: `Información no disponible: '${requestedInfo}'`
            });
          }
        }
      }
    });
  }, [
    selectedClient,
    conversationStage,
    setConversationStage,
    addSystemMessage,
    addLegalInformation,
    onFeedback
  ]);

  // Process initial message to adapt to agent instructions
  const processFirstMessage = (
    message: string,
    customerName: string
  ): string => {
    const getSalutation = () => {
      const hour = new Date().getHours();
      if (hour < 13) return 'Buenos días';
      if (hour < 20) return 'Buenas tardes';
      return 'Buenas noches';
    };

    return message
      .replace('{{salutationDayTime}}', getSalutation())
      .replace('{{customerName}}', customerName || '');
  };

  // Conversation hook configuration
  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);

      // Add system message
      addSystemMessage('info', 'Conexión establecida con el agente');
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      if (onFeedback) {
        onFeedback({
          type: 'info',
          message: 'Llamada finalizada',
          success: true
        });
      }
      setIsInitialized(false);
      setIsLoading(false);

      // Add system message
      addSystemMessage('info', 'Llamada finalizada');
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }

      // Add system message
      addSystemMessage('error', `Error: ${message}`);
    },
    onMessage: message => {
      // Add message to conversation with timestamp
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message,
          timestamp: new Date()
        }
      ]);

      // Automatic scrolling
      setTimeout(scrollToBottom, 0);
    },
    onModeChange: ({ mode }: ConversationMode) => {
      // console.log('Mode changed:', mode);
      setIsListening(mode === 'listening');
    },
    // Add client tools configuration
    clientTools: clientToolsConfig
  });

  // Request microphone permission
  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }

      // Add system message
      addSystemMessage('error', 'Permiso de micrófono denegado');

      return false;
    }
  };

  // Start conversation
  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);

      // Update conversation stage
      setConversationStage('in_conversation');

      // End previous session if exists
      await conversation.endSession();

      // Get signed URL for agent
      const signedUrl = await getHVASignedUrl(agentId);

      // Clear previous messages
      setMessages([]);

      // Start session with signed URL
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: agentInstructions.instructions
                ? `${prompt} ${agentInstructions.instructions}`
                : prompt
            },
            firstMessage: processFirstMessage(firstMessage, clientName)
          },
          tts: {
            voiceId: voice
          }
        }
      });

      // Add system message
      addSystemMessage('info', 'Conversación iniciada');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }

      // Add system message
      addSystemMessage(
        'error',
        `Error al iniciar la conversación: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );

      setIsLoading(false);
    }
  }, [
    agentId,
    conversation,
    hasPermission,
    onError,
    setConversationStage,
    voice,
    prompt,
    firstMessage,
    clientName,
    addSystemMessage,
    agentInstructions
  ]);

  // End conversation
  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
      setConversationStage('finished');
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError, setConversationStage]);

  // Effect to initialize scroll when messages mount
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Effect to access microphone when component mounts
  useEffect(() => {
    void requestMicPermission();

    // Cleanup when unmounting
    return () => {
      if (isInitialized) {
        // Use a synchronous version to avoid promises during unmounting
        try {
          conversation.endSession().catch(err => {
            // Only log the error, don't update state
            console.error('Error during cleanup:', err);
          });
        } catch (error) {
          console.error('Failed during cleanup:', error);
        }
      }
    };
  }, []);

  // Render component
  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div
            className={`d-flex ${
              isInitialized ? 'bg-danger' : 'bg-primary'
            } rounded-5 w-100 justify-content-center`}
          >
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                Finalizar
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Conectado:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Micrófono:
                    {isListening ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm flex-grow-1"
        style={{
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          height: '280px'
        }}
      >
        {messages.length > 0 ? (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => {
              // Determine icon based on message context
              let icon = null;
              if (msg.source === 'ai') {
                const messageText = msg.message.toLowerCase();
                if (
                  messageText.includes('legal') ||
                  messageText.includes('ley') ||
                  messageText.includes('normativa')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faGavel}
                      className="text-primary me-2"
                    />
                  );
                }
              }

              return (
                <div
                  key={index}
                  className={`p-2 rounded border mb-2 ${
                    msg.source === 'user'
                      ? 'bg-body mx-10 me-2'
                      : 'bg-body-secondary mx-2 me-10'
                  }`}
                >
                  <small className="text-muted fs-10 d-block mb-1">
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </small>
                  <strong>
                    {msg.source === 'user' ? 'Cliente' : agentName}:
                  </strong>
                  &nbsp;
                  <span>
                    {icon}
                    {msg.message}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted p-5">
            {isLoading ? (
              <p>Connecting with agent...</p>
            ) : (
              <p>Pulse "Iniciar Llamada" para comenzar la conversación</p>
            )}
          </div>
        )}
      </div>

      {!hasPermission && (
        <div className="alert alert-warning mt-3 mb-0 p-2 fs-9">
          <FontAwesomeIcon icon={faMicrophoneSlash} className="me-2" />
          Se requiere permiso de micrófono para la conversación
        </div>
      )}
    </div>
  );
};

export default AuditWidget;
